(() => {
  const TRIGGER_ATTRIBUTE = 'data-hd-inbox-trigger';
  const BOUND_ATTRIBUTE = 'data-hd-inbox-bound';
  const SIZING_LABEL_PATTERN = /\bget\s+(?:size|sizing)\s+(?:help|support)\b/i;
  const RETRY_DELAY = 125;
  const MAX_ATTEMPTS = 80;
  let retryTimer = null;

  const normalizeText = (value) => (value || '').replace(/\s+/g, ' ').trim();

  const isSizingTrigger = (element) =>
    element instanceof Element &&
    (element.hasAttribute(TRIGGER_ATTRIBUTE) ||
      SIZING_LABEL_PATTERN.test(normalizeText(element.textContent)));

  const getAccessibleWindows = () => {
    const windows = [window];

    for (const candidate of [window.parent, window.top]) {
      try {
        if (candidate && !windows.includes(candidate) && candidate.document) windows.push(candidate);
      } catch (error) {
        // Cross-origin preview wrappers cannot be inspected.
      }
    }

    return windows;
  };

  const getAccessibleDocuments = () =>
    getAccessibleWindows()
      .map((candidate) => {
        try {
          return candidate.document;
        } catch (error) {
          return null;
        }
      })
      .filter(Boolean);

  const launcherSelectors = [
    '#dummy-chat-button',
    '[data-spec="toggle-button"]',
    'button.chat-toggle',
    'button[aria-controls="chat-ui"]',
    '[aria-controls="chat-ui"]',
    'button[aria-label*="open chat" i]',
    'button[aria-label*="chat" i]',
    'button[aria-label*="support" i]'
  ];

  const queryLauncher = (root) => {
    if (!root || typeof root.querySelector !== 'function') return null;

    for (const selector of launcherSelectors) {
      try {
        const match = root.querySelector(selector);
        if (match) return match;
      } catch (error) {
        // Continue to the next known Shopify Inbox selector.
      }
    }

    return null;
  };

  const clickNode = (node) => {
    if (!node || typeof node.click !== 'function') return false;

    try {
      node.click();
      return true;
    } catch (error) {
      try {
        const eventWindow = node.ownerDocument?.defaultView || window;
        const EventConstructor = eventWindow.MouseEvent || MouseEvent;
        node.dispatchEvent(
          new EventConstructor('click', {
            bubbles: true,
            cancelable: true,
            composed: true,
            view: eventWindow
          })
        );
        return true;
      } catch (dispatchError) {
        return false;
      }
    }
  };

  const hostLooksOpen = (host) => {
    if (!host || typeof host.getAttribute !== 'function') return false;

    if (
      host.getAttribute('is-open') === 'true' ||
      host.getAttribute('open') === 'true' ||
      host.getAttribute('aria-expanded') === 'true'
    ) {
      return true;
    }

    const toggle = queryLauncher(host.shadowRoot) || queryLauncher(host);
    if (!toggle) return false;

    const ariaLabel = normalizeText(toggle.getAttribute?.('aria-label'));
    return (
      toggle.getAttribute?.('aria-expanded') === 'true' ||
      /\b(close|minimi[sz]e|return to chat)\b/i.test(ariaLabel)
    );
  };

  const getInboxHosts = () => {
    const hosts = [];

    for (const currentDocument of getAccessibleDocuments()) {
      const candidates = [
        currentDocument.getElementById('ShopifyChat'),
        currentDocument.getElementById('shopify-chat'),
        currentDocument.querySelector('inbox-online-store-chat#ShopifyChat'),
        currentDocument.querySelector('inbox-online-store-chat')
      ];

      for (const candidate of candidates) {
        if (!candidate || candidate.tagName === 'IFRAME' || hosts.includes(candidate)) continue;
        hosts.push(candidate);
      }
    }

    return hosts;
  };

  const inboxIsOpen = () => getInboxHosts().some(hostLooksOpen);

  const openFromLoadedHost = () => {
    for (const host of getInboxHosts()) {
      if (hostLooksOpen(host)) return true;

      const launcher = queryLauncher(host.shadowRoot) || queryLauncher(host);
      if (clickNode(launcher)) return true;
    }

    return false;
  };

  const openFromIframe = (frame) => {
    if (!frame) return false;

    try {
      const frameDocument = frame.contentDocument || frame.contentWindow?.document;
      const launcher = queryLauncher(frameDocument);
      return clickNode(launcher);
    } catch (error) {
      return false;
    }
  };

  const openFromKnownIframes = () => {
    for (const currentDocument of getAccessibleDocuments()) {
      const frames = currentDocument.querySelectorAll(
        'iframe#dummy-chat-button-iframe, iframe#ShopifyChat, iframe#shopify-chat, iframe[id*="shopify-chat" i], iframe[title*="chat" i], iframe[title*="support" i]'
      );

      for (const frame of frames) {
        if (openFromIframe(frame)) return true;
      }
    }

    return false;
  };

  const openFromPublicObject = () => {
    for (const currentWindow of getAccessibleWindows()) {
      const candidates = [
        currentWindow.ShopifyChat,
        currentWindow.ShopifyInbox,
        currentWindow.Shopify?.chat,
        currentWindow.Shopify?.inbox
      ].filter(Boolean);

      for (const candidate of candidates) {
        for (const method of ['open', 'openChat', 'show']) {
          if (typeof candidate?.[method] !== 'function') continue;

          try {
            candidate[method]();
            return true;
          } catch (error) {
            // Continue to the rendered Shopify Inbox launchers.
          }
        }
      }
    }

    return false;
  };

  const openShopifyInboxOnce = () => {
    if (inboxIsOpen()) return true;

    if (openFromLoadedHost()) return true;
    if (openFromKnownIframes()) return true;

    for (const currentDocument of getAccessibleDocuments()) {
      if (clickNode(currentDocument.getElementById('dummy-chat-button'))) return true;
    }

    return openFromPublicObject();
  };

  const showManualFallback = () => {
    if (document.getElementById('hd-inbox-fallback-notice')) return;

    const notice = document.createElement('div');
    notice.id = 'hd-inbox-fallback-notice';
    notice.setAttribute('role', 'status');
    notice.innerHTML =
      '<strong>Open Support</strong><span>Tap the Shopify Support button at the lower right.</span>';
    notice.style.cssText =
      'position:fixed;right:18px;bottom:105px;z-index:2147483646;max-width:300px;padding:14px 16px;background:#0E442B;color:#fff;border-radius:4px;box-shadow:0 10px 30px rgba(0,0,0,.18);font:600 14px/1.35 inherit;display:grid;gap:4px;';

    const detail = notice.querySelector('span');
    if (detail) detail.style.cssText = 'font-size:12px;font-weight:400;opacity:.9;';

    document.body.appendChild(notice);
    window.setTimeout(() => notice.remove(), 6000);
  };

  const openWithRetry = (trigger) => {
    if (retryTimer) {
      window.clearTimeout(retryTimer);
      retryTimer = null;
    }

    if (openShopifyInboxOnce()) return;

    trigger?.setAttribute('aria-busy', 'true');
    let attempts = 0;

    const tryOpen = () => {
      if (openShopifyInboxOnce()) {
        trigger?.removeAttribute('aria-busy');
        retryTimer = null;
        return;
      }

      attempts += 1;
      if (attempts >= MAX_ATTEMPTS) {
        trigger?.removeAttribute('aria-busy');
        retryTimer = null;
        showManualFallback();
        return;
      }

      retryTimer = window.setTimeout(tryOpen, RETRY_DELAY);
    };

    tryOpen();
  };

  const handleSizingClick = (event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    event.stopPropagation();
    openWithRetry(event.currentTarget);
  };

  const bindTrigger = (element) => {
    if (!isSizingTrigger(element) || element.hasAttribute(BOUND_ATTRIBUTE)) return;

    element.setAttribute(TRIGGER_ATTRIBUTE, '');
    element.setAttribute(BOUND_ATTRIBUTE, '');
    element.setAttribute('aria-haspopup', 'dialog');

    if (element instanceof HTMLAnchorElement) {
      element.setAttribute('href', '#shopify-inbox-support');
    }

    element.addEventListener('click', handleSizingClick, true);
  };

  const bindTree = (root) => {
    if (!(root instanceof Element || root instanceof Document)) return;

    if (root instanceof Element && root.matches('a, button')) bindTrigger(root);
    root.querySelectorAll?.('a, button').forEach(bindTrigger);
  };

  const initialize = () => {
    bindTree(document);

    const observer = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node instanceof Element) bindTree(node);
        }
      }
    });

    if (document.body) observer.observe(document.body, { childList: true, subtree: true });
  };

  window.HexaDentOpenShopifyInbox = openWithRetry;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
